import { loadJourneyContent } from '../../../entities/journey'
import { nextQuestion, questions, quizSubmission, type Answer } from './breakupQuiz'
import { useDraftStore } from './draftStore'
import type { OnboardingPayload, OnboardingScenario } from './types'

const COOKIE = 'humanteq_break_up_quiz'

// Shared wire format with the landing: Base64url(JSON({ v: 1, answers: [[id, choice]] })).
export function decodeLandingQuiz(value: string): Answer[] | null {
  try {
    if (value.length > 2048 || !/^[\w-]+$/.test(value)) return null
    const data = JSON.parse(atob(value.replace(/-/g, '+').replace(/_/g, '/')))
    if (data?.v !== 1 || !Array.isArray(data.answers)) return null
    const answers: Answer[] = []
    for (const pair of data.answers) {
      const expected = nextQuestion(answers)
      if (!expected || !Array.isArray(pair) || pair.length !== 2 || pair[0] !== expected || !Number.isInteger(pair[1]) || pair[1] < 0 || pair[1] >= questions[expected].options.length) return null
      answers.push({ question: expected, choice: pair[1] })
    }
    return nextQuestion(answers) === null ? answers : null
  } catch { return null }
}

export function clearLandingQuizCookie() {
  const domain = location.hostname === 'humanteq.com' || location.hostname.endsWith('.humanteq.com') ? '; Domain=humanteq.com' : ''
  document.cookie = `${COOKIE}=; Max-Age=0; Path=/${domain}; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`
}

export function captureLandingQuiz(scenario: Pick<OnboardingScenario, 'id' | 'version'>): boolean {
  const value = document.cookie.split('; ').find((entry) => entry.startsWith(`${COOKIE}=`))?.slice(COOKIE.length + 1)
  if (!value) return false
  const answers = decodeLandingQuiz(value)
  if (answers) {
    const store = useDraftStore.getState()
    // A new landing result starts a fresh guest introduction, never an account update.
    store.clear()
    const draft = store.ensure('break_up', scenario.id, scenario.version, null)
    store.update(draft.id, { custom: { landing_quiz_answers: answers } })
  }
  clearLandingQuizCookie()
  return Boolean(answers)
}

export async function withLandingQuiz(payload: OnboardingPayload): Promise<OnboardingPayload> {
  const answers = payload.custom?.landing_quiz_answers as Answer[] | undefined
  if (!answers) return payload
  const submission = quizSubmission(answers, await loadJourneyContent())
  return { ...payload, journey: submission.journey, tags: [...new Set([...(payload.tags ?? []), ...submission.tags])] }
}
